/**
 * 画布图片生成器
 *
 * 走画布应用专属端点 api.generateCanvasImage（source='canvas'）+ 通用 pollJobStatus，
 * 画布只负责把结果写回到节点。
 *
 * 流程：
 *  1. 在调用前先把 generate_image 节点 data.generating 置为 true。
 *  2. 提交后获得 jobId，立即在画布右侧创建占位 image 节点（status='processing'）。
 *  3. pollJobStatus 完成后，把图片 URL 写回对应 image 节点的 data。
 *  4. 失败时把 image 节点 data.status='failed'，并保留 errorMessage（仅前端展示通用文案）。
 *
 * 注意：本模块不直接消费"用户积分余额"，由后端按现有逻辑扣减；前端只需要捕获错误。
 */
import { api } from '../../../services/api';
import type { JobStatus, GenerationMode } from '../../../services/api';
import toast from '../../../components/Toast';
import { boardActions, getBoardState } from '../state/boardStore';
import { aggregateUpstream, collectUpstreamImageAssetGroups } from '../state/upstream';
import { resolveAtReferences } from '../atReference';
import { CANVAS_NODE_REMOVED, makeNode, nid } from '../utils';
import { normalizeJobStatus } from '../utils/jobStatus';
import { toPersistableMediaUrl } from '../utils/persistableMediaUrl';
import { validateImageReferenceQuota } from '../utils/referenceImageQuota';
import { getCachedImageModels } from '../modelCache';
import { resolveBlobUrlsForApi } from '../imageStorage';
import { reuseAutoChildSlot } from './resultSlots';
import type { CanvasNode, GenerateImageNodeData, ImageGroupNodeData, ImageNodeData } from '../types';

/** 每个生图节点的进行中任务追踪：abort 旧轮询 + 版本号防止旧任务 finally 覆盖新任务状态 */
const imageAbortMap = new Map<string, AbortController>();
const imageVersionMap = new Map<string, number>();

/** 把 modelId 映射为模型展示名；缓存未加载或未命中时回退 modelId 本身 */
async function resolveImageModelLabel(modelId: string): Promise<string> {
  try {
    const models = await getCachedImageModels();
    return models.find((m) => m.id === modelId)?.name ?? modelId;
  } catch {
    return modelId;
  }
}

interface RunImageGenerationOptions {
  /** 触发的 generate_image 节点 */
  generateNode: CanvasNode<GenerateImageNodeData>;
  /** 上游聚合后的最终 prompt（已合并 generate_node.data.prompt 与上游文本节点） */
  prompt: string;
  /** 上游图片节点解析得到的参考图列表（URL/storage key） */
  referenceImages: string[];
  /** 蒙版图 base64（inpaint 模式使用） */
  maskImage?: string;
}

/** 默认参数（生成配置节点未填写时的兜底） */
const DEFAULTS = {
  aspectRatio: 'auto',
  quality: 'low',
  count: 1,
  mode: 'text2img' as GenerationMode,
};

/**
 * 在 generateNode 右侧依次摆放 N 个占位 image 节点。
 * 列宽 = 节点宽度 + 24，行高 = 节点高度 + 24。
 */
function planChildPositions(
  generateNode: CanvasNode,
  count: number
): { x: number; y: number }[] {
  const baseX = generateNode.x + generateNode.width + 60;
  const baseY = generateNode.y;
  const cols = Math.min(count, 2);
  const colWidth = 320 + 24;
  const rowHeight = 320 + 24;
  const positions: { x: number; y: number }[] = [];
  for (let i = 0; i < count; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    positions.push({ x: baseX + col * colWidth, y: baseY + row * rowHeight });
  }
  return positions;
}

/** 读取图片组节点当前数据；节点被撤销/删除时返回 null */
function readImageGroup(groupNodeId: string): ImageGroupNodeData | null {
  const node = getBoardState().nodes.find((n) => n.id === groupNodeId);
  return node ? (node.data as ImageGroupNodeData) : null;
}

/** 按索引写回图片组单张任务的提交 ID / 状态 / 结果（读-改-写整组数组，同步执行无竞态） */
export function patchImageGroupIndex(
  groupNodeId: string,
  index: number,
  patch: { jobId?: string; status?: string; imageUrl?: string | null }
): void {
  const data = readImageGroup(groupNodeId);
  if (!data) return;
  const next: Record<string, unknown> = {};
  if (patch.jobId !== undefined) {
    const jobIds = [...(data.jobIds ?? [])];
    jobIds[index] = patch.jobId;
    next.jobIds = jobIds;
  }
  if (patch.status !== undefined) {
    const statuses = [...(data.statuses ?? [])];
    statuses[index] = patch.status;
    next.statuses = statuses;
  }
  if (patch.imageUrl !== undefined) {
    const imageUrls = [...(data.imageUrls ?? [])];
    imageUrls[index] = patch.imageUrl;
    next.imageUrls = imageUrls;
  }
  if (Object.keys(next).length === 0) return;
  boardActions.updateNodeData(groupNodeId, next);
}

/** 轮询回调：把任务终态/结果写入组内对应格子；processing 中间态不写（避免 2s 一次空写） */
export function writeGroupIndexFromJob(groupNodeId: string, index: number, job: JobStatus): void {
  const status = normalizeJobStatus(job.status);
  if (status !== 'completed' && status !== 'failed') return;
  const data = readImageGroup(groupNodeId);
  if (!data) return;
  if ((data.statuses ?? [])[index] === status) return;
  patchImageGroupIndex(groupNodeId, index, {
    status,
    imageUrl: status === 'completed' ? toPersistableMediaUrl(job.resultUrl) : undefined,
  });
}

/** 按索引删除组内一个槽位（jobIds/statuses/imageUrls 同步 splice），供 agent 删除计划项时三方对齐 */
export function removeImageGroupSlot(groupNodeId: string, index: number): void {
  const data = readImageGroup(groupNodeId);
  if (!data) return;
  const jobIds = [...(data.jobIds ?? [])];
  if (index < 0 || index >= jobIds.length) return;
  const statuses = [...(data.statuses ?? [])];
  const imageUrls = [...(data.imageUrls ?? [])];
  jobIds.splice(index, 1);
  statuses.splice(index, 1);
  imageUrls.splice(index, 1);
  boardActions.updateNodeData(groupNodeId, { jobIds, statuses, imageUrls });
}

/** 整组终态回写：全部失败标 failed，否则 completed（部分失败由格子级状态展示） */
export function finalizeImageGroup(groupNodeId: string): void {
  const data = readImageGroup(groupNodeId);
  if (!data) return;
  const statuses = data.statuses ?? [];
  const allFailed = statuses.length > 0 && statuses.every((s) => s === 'failed');
  boardActions.updateNodeData(groupNodeId, { status: allFailed ? 'failed' : 'completed' });
}

/**
 * 刷新后恢复：续接图片组内未终态任务的轮询。
 * 全部格子已终态但组仍 processing（刷新落在写回窗口）时直接落终态，不留僵尸组。
 */
export async function resumeImageGroupJobs(groupNodeId: string): Promise<void> {
  const data = readImageGroup(groupNodeId);
  if (!data) return;
  const jobIds = data.jobIds ?? [];
  const statuses = data.statuses ?? [];
  // 无 jobId 且未到终态的槽位（提交前刷新）：无从续接轮询，按中断失败收敛。
  // 否则 finalizeImageGroup 会把组误判为 completed，agent 批次的对应计划项
  // 既不提交也无报错，且 failedCount === 0 时重试入口不会出现
  const hasOrphanSlot = statuses.some(
    (s, i) => !jobIds[i] && !['completed', 'failed'].includes(s ?? '')
  );
  if (hasOrphanSlot) {
    boardActions.updateNodeData(groupNodeId, {
      statuses: statuses.map((s, i) =>
        !jobIds[i] && !['completed', 'failed'].includes(s ?? '') ? 'failed' : s
      ),
    });
  }
  const pending = jobIds
    .map((jobId, i) => ({ jobId, i }))
    .filter((x): x is { jobId: string; i: number } =>
      !!x.jobId && !['completed', 'failed'].includes((data.statuses ?? [])[x.i] ?? '')
    );
  if (pending.length === 0) {
    finalizeImageGroup(groupNodeId);
    return;
  }
  await Promise.allSettled(
    pending.map(async ({ jobId, i }) => {
      try {
        await api.pollJobStatus(jobId, (job) => {
          if (!getBoardState().nodes.some((n) => n.id === groupNodeId)) {
            throw new Error(CANVAS_NODE_REMOVED);
          }
          writeGroupIndexFromJob(groupNodeId, i, job);
        });
      } catch (err) {
        patchImageGroupIndex(groupNodeId, i, { status: 'failed' });
        if (err instanceof Error && err.message === CANVAS_NODE_REMOVED) return;
        throw err;
      }
    })
  );
  finalizeImageGroup(groupNodeId);
}

export async function runImageGeneration(opts: RunImageGenerationOptions): Promise<void> {
  const { generateNode, prompt, referenceImages, maskImage } = opts;

  // 重新生成时取消旧轮询：abort 旧 AbortController，旧轮询静默终止
  imageAbortMap.get(generateNode.id)?.abort();
  const abortController = new AbortController();
  imageAbortMap.set(generateNode.id, abortController);
  const myVersion = (imageVersionMap.get(generateNode.id) ?? 0) + 1;
  imageVersionMap.set(generateNode.id, myVersion);
  const isCurrentVersion = () => imageVersionMap.get(generateNode.id) === myVersion;

  const data = generateNode.data;

  if (!data.modelId) {
    throw new Error('请先选择图片模型');
  }
  if (!prompt.trim()) {
    throw new Error('请填写提示词');
  }

  // @引用解析（生图节点专属）：@图N 按**稳定图号**解析（与连线位置无关，
  // 删除其他节点不漂移）；提示词含标记时参考图切换为标记显式引用的上游资产
  // （按标记出现顺序去重），标记替换为「参考图」后随提示词提交；
  // 无标记时维持连线全量自动聚合的现行为
  const { nodes: graphNodes, edges: graphEdges } = getBoardState();
  const knownRefNos = new Set(
    graphNodes
      .filter((n) => n.type === 'image' || n.type === 'image_group')
      .map((n) => (n.data as Record<string, unknown>).refNo)
      .filter((v): v is number => typeof v === 'number')
  );
  const atResult = resolveAtReferences(
    prompt,
    collectUpstreamImageAssetGroups(generateNode.id, graphNodes, graphEdges),
    knownRefNos
  );
  if (atResult && atResult.warnings.length > 0) {
    toast(atResult.warnings.join('；'));
  }
  const finalPrompt = atResult ? atResult.prompt : prompt;
  const baseReferenceImages = atResult ? atResult.referenceImages : referenceImages;

  const mode: GenerationMode = (data.mode || DEFAULTS.mode) as GenerationMode;
  const aspectRatio = data.aspectRatio || DEFAULTS.aspectRatio;
  const quality = data.quality || DEFAULTS.quality;
  const count = Math.max(1, Math.min(8, data.count ?? DEFAULTS.count));

  // 图生图无参考图兜底（链式执行等绕过按钮的入口；按钮侧已按有无图上游禁用模式）。
  // 同步可判的校验全部前置到置位之前，保证这些 throw 不需要复位标志
  if (mode === 'img2img' && baseReferenceImages.length === 0) {
    throw new Error('图生图模式需要连接参考图（图片节点或图片组），或切换为文生图');
  }

  // 标记 generate 节点进入生成中状态（仅在内存里；持久化由 autoSave 接管）。
  // 置位必须先于下方所有 await：守卫读到的 generating 与置位之间隔着任何
  // await 都会被并发入口（另一条链 / 执行全部按钮）穿透，重复提交、重复扣积分
  boardActions.updateNodeData(generateNode.id, { generating: true });

  let resolvedReferenceImages: string[];
  let resolvedMaskImage: string | undefined;
  try {
    // 本地上传的图片是 blob: URL，后端无法访问，提交前统一解析为 data URL（失败条目被过滤）
    resolvedReferenceImages = await resolveBlobUrlsForApi(baseReferenceImages);

    if (mode === 'inpaint') {
      if (!resolvedReferenceImages.length) {
        throw new Error('局部重绘模式需要连接一张参考图');
      }
      if (!maskImage) {
        throw new Error('局部重绘模式需要先绘制蒙版');
      }
    }

    // 参考图数量配额兜底（与节点置灰同一规则）：图片后端不校验数量，
    // 链式执行等绕过按钮的入口以这里为实际拦截线。计数用连线聚合的原始
    // 数量，与节点置灰口径一致；模型缓存取不到能力时乐观放行。
    const quotaCaps = await getCachedImageModels()
      .then((list) => list.find((m) => m.id === data.modelId)?.modeCapabilities)
      .catch(() => undefined);
    const quotaError = validateImageReferenceQuota(mode, baseReferenceImages.length, quotaCaps);
    if (quotaError) {
      throw new Error(quotaError);
    }

    // 蒙版可能来自 IndexedDB（blob: URL），提交前同样转为 data URL；旧数据（data URL）原样透传
    if (mode === 'inpaint' && maskImage) {
      resolvedMaskImage = (await resolveBlobUrlsForApi([maskImage]))[0];
      if (!resolvedMaskImage) {
        throw new Error('蒙版图片解析失败，请重新绘制蒙版');
      }
    }
  } catch (err) {
    // 准备阶段失败：复位生成中标志（批量/单张分支内部的 catch/finally
    // 负责提交后的复位与结果落败，不经过这里）
    if (isCurrentVersion()) {
      boardActions.updateNodeData(generateNode.id, { generating: false });
    }
    throw err;
  }

  // 批量（count > 1）：结果收进单个图片组节点（组内轮播/设主图/拆分单张），
  // 画布只占一格；单张保持独立 image 节点不变
  if (count > 1) {
    const modelLabel = await resolveImageModelLabel(data.modelId!);
    const emptyGroupData = () => ({
      jobIds: Array.from({ length: count }, () => null),
      imageUrls: Array.from({ length: count }, () => null),
      statuses: Array.from({ length: count }, () => 'processing'),
      status: 'processing',
      modelLabel,
    });
    // 复用已有图片组卡片（原地清空，保留位置与手工连线），没有才新建
    const groupNodeId = reuseAutoChildSlot(generateNode, 'image_group', {
      create: () =>
        makeNode<ImageGroupNodeData>(
          'image_group',
          planChildPositions(generateNode, 1)[0],
          { ...emptyGroupData(), _autoGenerated: true } as ImageGroupNodeData & { _autoGenerated: boolean },
          undefined,
          generateNode.zIndex
        ),
      reset: (nodeId) => boardActions.updateNodeData(nodeId, emptyGroupData()),
    });

    const groupTasks = Array.from({ length: count }, (_, i) => (async () => {
      try {
        const resp = await api.generateCanvasImage({
          model: data.modelId!,
          prompt: finalPrompt,
          aspectRatio,
          quality,
          n: 1,
          mode,
          referenceImages: resolvedReferenceImages.length > 0 ? resolvedReferenceImages : undefined,
          maskImage: resolvedMaskImage,
          clientRequestId: nid(),
        });
        patchImageGroupIndex(groupNodeId, i, { jobId: resp.jobId });
        await api.pollJobStatus(resp.jobId, (job: JobStatus) => {
          // 组节点被撤销/删除时中断轮询，避免幽灵写入
          if (!getBoardState().nodes.some((n) => n.id === groupNodeId)) {
            throw new Error(CANVAS_NODE_REMOVED);
          }
          writeGroupIndexFromJob(groupNodeId, i, job);
        }, 2000, 300, abortController.signal);
      } catch (err) {
        patchImageGroupIndex(groupNodeId, i, { status: 'failed' });
        // abort（重新生成取消旧轮询）不算失败；组被删除属于主动取消；其余错误上抛
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (err instanceof Error && err.message === CANVAS_NODE_REMOVED) return;
        throw err;
      }
    })());

    try {
      const results = await Promise.allSettled(groupTasks);
      const firstRejected = results.find(
        (r): r is PromiseRejectedResult => r.status === 'rejected'
      );
      if (firstRejected) throw firstRejected.reason;
    } finally {
      finalizeImageGroup(groupNodeId);
      // 旧任务被 abort 时不应覆盖新任务的 generating 标记
      if (!isCurrentVersion()) return;
      const latest = getBoardState().nodes.find((n) => n.id === groupNodeId);
      const groupJobIds = latest
        ? ((latest.data as ImageGroupNodeData).jobIds ?? []).filter((id): id is string => !!id)
        : [];
      boardActions.updateNodeData(generateNode.id, {
        generating: false,
        lastJobIds: groupJobIds,
      });
    }
    return;
  }

  // 单张：复用已有结果卡片（原地清空，保留位置与手工连线），没有才新建
  const modelLabel = await resolveImageModelLabel(data.modelId!);
  const slotNodeId = reuseAutoChildSlot(generateNode, 'image', {
    create: () =>
      makeNode<ImageNodeData>(
        'image',
        planChildPositions(generateNode, 1)[0],
        {
          status: 'processing',
          prompt: finalPrompt,
          imageUrl: null,
          jobId: null,
          modelLabel,
          aspectRatio: data.aspectRatio ?? null,
          _autoGenerated: true,
        } as ImageNodeData & { _autoGenerated: boolean },
        undefined,
        generateNode.zIndex
      ),
    reset: (nodeId) =>
      boardActions.updateNodeData(nodeId, {
        status: 'processing',
        prompt: finalPrompt,
        imageUrl: null,
        jobId: null,
        errorMessage: null,
        modelLabel,
        _imageStorageKey: null,
        aspectRatio: data.aspectRatio ?? null,
      }),
  });

  // 并发提交生成任务（单张只有一个任务，保留 allSettled 结构与批量路径对齐）
  const tasks = [slotNodeId].map(async (slotId) => {
    try {
      const resp = await api.generateCanvasImage({
        model: data.modelId!,
        prompt: finalPrompt,
        aspectRatio,
        quality,
        n: 1,
        mode,
        referenceImages: resolvedReferenceImages.length > 0 ? resolvedReferenceImages : undefined,
        maskImage: resolvedMaskImage,
        clientRequestId: nid(),
      });
      // 把 jobId 写回结果节点
      boardActions.updateNodeData(slotId, { jobId: resp.jobId });

      // 轮询状态
      await api.pollJobStatus(resp.jobId, (job: JobStatus) => {
        // 结果节点被撤销/删除时中断轮询，避免幽灵写入
        if (!getBoardState().nodes.some((n) => n.id === slotId)) {
          throw new Error(CANVAS_NODE_REMOVED);
        }
        const imageUrl = toPersistableMediaUrl(job.resultUrl);
        // 状态先经归一化（submitting/queued 等收敛为 processing），避免未知中间态把节点渲染成空节点
        const status = normalizeJobStatus(job.status);
        // 如果还在处理中但有 base64Data，状态保持 processing 但 imageUrl 已有值，实现乐观预览
        boardActions.updateNodeData(slotId, {
          status,
          imageUrl,
          prompt: job.prompt,
        });
      }, 2000, 300, abortController.signal);
    } catch (err) {
      // abort（重新生成取消旧轮询）静默终止，不标记失败
      if (err instanceof DOMException && err.name === 'AbortError') return;
      const msg = err instanceof Error ? err.message : '生成失败';
      boardActions.updateNodeData(slotId, {
        status: 'failed',
        errorMessage: msg,
      });
      // 结果节点被删除属于主动取消，不算失败；其余错误上抛，让链式执行中断
      if (err instanceof Error && err.message === CANVAS_NODE_REMOVED) return;
      throw err;
    }
  });

  try {
    const results = await Promise.allSettled(tasks);
    const firstRejected = results.find(
      (r): r is PromiseRejectedResult => r.status === 'rejected'
    );
    // 链路语义：任一子任务真实失败时上抛中断后续节点（节点内已标记失败态，调用方负责 toast）
    if (firstRejected) throw firstRejected.reason;
  } finally {
    // 旧任务被 abort 时不应覆盖新任务的 generating 标记
    if (!isCurrentVersion()) return;
    boardActions.updateNodeData(generateNode.id, {
      generating: false,
      lastJobIds: [slotNodeId]
        .map((id) => {
          const latest = getBoardState().nodes.find((nn) => nn.id === id);
          return (latest?.data as ImageNodeData | undefined)?.jobId ?? null;
        })
        .filter((id): id is string => !!id),
    });
  }
}

/**
 * 单张重试：对单个 image 节点按其上游 generate_image 节点的当前参数重新提交一个任务，
 * 结果写回本节点（只影响这一张，不动同批其他节点）。
 *
 * 参数来源：模型/模式/比例/质量取上游生图节点的当前配置；
 * prompt 优先用本节点记录的提交时 prompt（服务端轮询时写回），缺失时回退上游聚合结果。
 */
export async function retryImageNode(imageNode: CanvasNode<ImageNodeData>): Promise<void> {
  const state = getBoardState();
  const genEdge = state.edges.find((e) => e.targetNodeId === imageNode.id);
  const genNode = genEdge
    ? (state.nodes.find(
        (n) => n.id === genEdge.sourceNodeId && n.type === 'generate_image'
      ) as CanvasNode<GenerateImageNodeData> | undefined)
    : undefined;
  if (!genNode) {
    throw new Error('未找到上游生图配置节点，无法重试');
  }
  if (genNode.data.generating) {
    throw new Error('上游生图节点正在生成中，请稍后再试');
  }

  const data = genNode.data;
  if (!data.modelId) throw new Error('请先在上游生图节点选择图片模型');

  const upstream = aggregateUpstream(genNode, state.nodes, state.edges);
  const prompt = (imageNode.data.prompt || upstream.prompt || '').trim();
  if (!prompt) throw new Error('缺少提示词，无法重试');

  const mode: GenerationMode = (data.mode || DEFAULTS.mode) as GenerationMode;
  const resolvedReferenceImages = await resolveBlobUrlsForApi(upstream.referenceImages);

  let resolvedMaskImage: string | undefined;
  if (mode === 'inpaint') {
    // 蒙版在上游生图节点数据里；重试沿用上游配置的蒙版
    const maskFromGen = (data.maskImage as string | null | undefined) || undefined;
    resolvedMaskImage = maskFromGen ? (await resolveBlobUrlsForApi([maskFromGen]))[0] : undefined;
    if (!resolvedReferenceImages.length) throw new Error('局部重绘模式需要连接一张参考图');
    if (!resolvedMaskImage) throw new Error('局部重绘模式需要先绘制蒙版');
  }

  const modelLabel = await resolveImageModelLabel(data.modelId);
  boardActions.updateNodeData(imageNode.id, { status: 'processing', errorMessage: null, modelLabel });

  try {
    const resp = await api.generateCanvasImage({
      model: data.modelId,
      prompt,
      aspectRatio: data.aspectRatio || DEFAULTS.aspectRatio,
      quality: data.quality || DEFAULTS.quality,
      n: 1,
      mode,
      referenceImages: resolvedReferenceImages.length > 0 ? resolvedReferenceImages : undefined,
      maskImage: resolvedMaskImage,
      clientRequestId: nid(),
    });
    boardActions.updateNodeData(imageNode.id, { jobId: resp.jobId });
    await api.pollJobStatus(resp.jobId, (job: JobStatus) => {
      if (!getBoardState().nodes.some((n) => n.id === imageNode.id)) {
        throw new Error(CANVAS_NODE_REMOVED);
      }
      boardActions.updateNodeData(imageNode.id, {
        status: normalizeJobStatus(job.status),
        imageUrl: toPersistableMediaUrl(job.resultUrl),
        prompt: job.prompt,
      });
    });
  } catch (err) {
    if (err instanceof Error && err.message === CANVAS_NODE_REMOVED) return;
    boardActions.updateNodeData(imageNode.id, {
      status: 'failed',
      errorMessage: err instanceof Error ? err.message : '生成失败',
    });
    throw err;
  }
}
